import { z } from 'zod'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'
import { ghlOpportunityStatusSchema } from '../enums/ghl-opportunity-status.enum.js'
import { ghlLinkViaSchema } from '../enums/ghl-link-via.enum.js'
import { ghlLinkActionSchema } from '../enums/ghl-link-action.enum.js'
import { ghlBackfillStatusSchema } from '../enums/ghl-backfill-status.enum.js'
import { ghlReviewReasonSchema } from '../enums/ghl-review-reason.enum.js'
import { GHL_FUNNEL_TARGETS } from '../enums/funnel-stage.enum.js'
import { appointmentsMetricDefinitionSchema, ghlNameSchema, leadsMetricDefinitionSchema } from './lead-stats.dto.js'
import { GHL_FUNNEL_PIPELINES_MAX } from '../constants/gohighlevel.js'

/**
 * GoHighLevel connections, as the venue's GoHighLevel page sees them. See
 * docs/gohighlevel.spec.md. **No shape here carries a key**: a key goes in once,
 * sealed, and only its last four characters ever come back.
 */

/** One pipeline stage, by GoHighLevel's own ids. */
export const ghlStageRefSchema = z.object({ pipelineId: z.string().min(1), stageId: z.string().min(1) })
export const ghlStageRefListSchema = z.array(ghlStageRefSchema).max(100)
export type GhlStageRef = z.infer<typeof ghlStageRefSchema>

/** The details copied into an event, each pointed at one of the account's contact fields. */
export const GHL_FIELD_SLOTS = ['guestCount', 'preferredDate', 'budget', 'partnerName'] as const
export const ghlFieldSlotSchema = z.enum(GHL_FIELD_SLOTS)
export type GhlFieldSlot = z.infer<typeof ghlFieldSlotSchema>

export const GHL_FIELD_SLOT_LABEL: Record<GhlFieldSlot, string> = {
  guestCount: 'Guest count',
  preferredDate: 'Preferred date',
  budget: 'Budget',
  partnerName: 'Partner’s name',
}

/** Slot → contact field id. A slot with no field is simply not copied. */
export const ghlFieldMapSchema = z
  .object({
    guestCount: z.string().min(1).optional(),
    preferredDate: z.string().min(1).optional(),
    budget: z.string().min(1).optional(),
    partnerName: z.string().min(1).optional(),
  })
export type GhlFieldMap = z.infer<typeof ghlFieldMapSchema>

/**
 * What one funnel stage is, in a GoHighLevel account: the leads that match a
 * definition, or the appointments on chosen calendars. The same definitions the
 * lead stats numbers use (docs/gohighlevel-stats.spec.md), by GoHighLevel's names.
 */
export const ghlFunnelSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('leads'), definition: leadsMetricDefinitionSchema }),
  z.object({ kind: z.literal('appointments'), definition: appointmentsMetricDefinitionSchema }),
])
export type GhlFunnelSource = z.infer<typeof ghlFunnelSourceSchema>

/**
 * Which of a connection's pipelines, stages, results, tags and calendars mean
 * which funnel stage (docs/funnel-stages.spec.md). A stage left out is "not
 * tracked" from this connection, never zero. Separate from lead stats: mapping
 * the funnel changes none of their numbers.
 */
export const ghlFunnelMapSchema = z.object({
  /** The pipelines whose leads are couples, by name. Empty means every pipeline. */
  pipelines: z.array(ghlNameSchema).max(GHL_FUNNEL_PIPELINES_MAX).default([]),
  stages: z.partialRecord(z.enum(GHL_FUNNEL_TARGETS), ghlFunnelSourceSchema).default({}),
})
export type GhlFunnelMap = z.infer<typeof ghlFunnelMapSchema>

export const ghlConnectionDtoSchema = z.object({
  id: z.string().uuid(),
  venueId: z.string().uuid(),
  locationId: z.string(),
  /** The sub-account's name as GoHighLevel last reported it. */
  locationName: z.string(),
  /** The last four characters of the key, to tell keys apart. Fewer when the key itself was shorter. */
  keyLast4: z.string().max(4),
  /** The track an event created from this connection's leads starts on. */
  trackId: z.string().uuid().nullable(),
  pickerStages: ghlStageRefListSchema,
  pickerStatuses: z.array(ghlOpportunityStatusSchema),
  fieldMap: ghlFieldMapSchema,
  /** Defaulted while an older API is still answering. */
  funnelMap: ghlFunnelMapSchema.default({ pipelines: [], stages: {} }),
  /** When GoHighLevel started refusing the key; null while it works. */
  failingSince: instantSchema.nullable(),
  lastCheckedAt: instantSchema.nullable(),
  createdAt: instantSchema,
})
export type GhlConnectionDto = z.infer<typeof ghlConnectionDtoSchema>

export const ghlVenueSettingsDtoSchema = z.object({
  venueId: z.string().uuid(),
  /** The company's format, which every venue uses: shown here, changed on the company's page. */
  nameFormat: z.string(),
  connections: z.array(ghlConnectionDtoSchema),
})
export type GhlVenueSettingsDto = z.infer<typeof ghlVenueSettingsDtoSchema>

/** The company's GoHighLevel page: what every venue shares. */
export const ghlCompanySettingsDtoSchema = z.object({
  /** The format every venue uses: the company's own, or the default. */
  nameFormat: z.string(),
  /** False while the company has never chosen one, so the page offers the suggestion. */
  nameFormatIsSet: z.boolean(),
  /** What the company's existing event names, across all its venues, suggest. */
  suggestedNameFormat: z.string(),
})
export type GhlCompanySettingsDto = z.infer<typeof ghlCompanySettingsDtoSchema>

/** Read from GoHighLevel when a connection's settings are opened. */
export const ghlConnectionOptionsDtoSchema = z.object({
  pipelines: z.array(
    z.object({ id: z.string(), name: z.string(), stages: z.array(z.object({ id: z.string(), name: z.string() })) }),
  ),
  contactFields: z.array(z.object({ id: z.string(), name: z.string(), fieldKey: z.string(), dataType: z.string() })),
  suggestedFieldMap: ghlFieldMapSchema,
  /** Stages ticked on the connection that GoHighLevel no longer has. */
  missingStages: ghlStageRefListSchema,
})
export type GhlConnectionOptionsDto = z.infer<typeof ghlConnectionOptionsDtoSchema>

/** One GoHighLevel opportunity an event is linked to, as the event's GoHighLevel section shows it. */
export const eventGhlLinkDtoSchema = z.object({
  locationId: z.string(),
  opportunityId: z.string(),
  contactId: z.string(),
  /** The connection it came through; null once that connection was removed. */
  connectionId: z.string().uuid().nullable(),
  /** The sub-account's name, while its connection exists. */
  locationName: z.string().nullable(),
  via: ghlLinkViaSchema,
  clefProbability: z.number().nullable(),
  linkedAt: instantSchema,
  linkedByName: z.string().nullable(),
  /** The contact's page in GoHighLevel. */
  openUrl: z.string().url(),
  /** Read live from GoHighLevel, never stored; null when it can't be reached. */
  couple: z.object({ name: z.string(), email: z.string().nullable() }).nullable(),
})
export type EventGhlLinkDto = z.infer<typeof eventGhlLinkDtoSchema>

/** A link made or taken off, for the event's history. */
export const eventGhlHistoryDtoSchema = z.object({
  action: ghlLinkActionSchema,
  /** How a link was made; null for one taken off. */
  via: ghlLinkViaSchema.nullable(),
  locationId: z.string(),
  opportunityId: z.string(),
  byName: z.string().nullable(),
  at: instantSchema,
})
export type EventGhlHistoryDto = z.infer<typeof eventGhlHistoryDtoSchema>

/** An event's GoHighLevel section: what it is linked to, and how that came about. */
export const eventGhlDtoSchema = z.object({
  links: z.array(eventGhlLinkDtoSchema),
  history: z.array(eventGhlHistoryDtoSchema),
  /** What couldn't be filled in when linking, said plainly; the links themselves were saved. */
  warnings: z.array(z.string()).default([]),
})
export type EventGhlDto = z.infer<typeof eventGhlDtoSchema>

/** Whether a venue's staff can be offered the lead picker. Says nothing about which sub-accounts. */
export const ghlAvailabilityDtoSchema = z.object({
  /** The venue has at least one connection. */
  connected: z.boolean(),
  /** At least one of them is working. */
  working: z.boolean(),
})
export type GhlAvailabilityDto = z.infer<typeof ghlAvailabilityDtoSchema>

/** The company's event name format, for staff creating an event. */
export const ghlNameFormatDtoSchema = z.object({ nameFormat: z.string() })
export type GhlNameFormatDto = z.infer<typeof ghlNameFormatDtoSchema>

/** One of a couple's opportunities, in one sub-account. */
export const ghlCoupleOpportunityDtoSchema = z.object({
  connectionId: z.string().uuid(),
  locationId: z.string(),
  locationName: z.string(),
  opportunityId: z.string(),
  contactId: z.string(),
  stageName: z.string(),
  status: ghlOpportunityStatusSchema,
  source: z.string().nullable(),
  leadCreatedAt: instantSchema,
})
export type GhlCoupleOpportunityDto = z.infer<typeof ghlCoupleOpportunityDtoSchema>

/**
 * A couple the picker offers: one row however many of the venue's sub-accounts
 * hold an opportunity for them, recognized by their email.
 */
export const ghlCoupleDtoSchema = z.object({
  /** Stable for the list: a hash of the email, or the contact when there is none. */
  key: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /** Most recent lead first. */
  opportunities: z.array(ghlCoupleOpportunityDtoSchema).min(1),
})
export type GhlCoupleDto = z.infer<typeof ghlCoupleDtoSchema>

export const ghlCoupleListDtoSchema = z.object({
  /** Couples without an event yet, most recent lead first; at most 500. */
  couples: z.array(ghlCoupleDtoSchema),
  /** More couples matched than were sent. */
  truncated: z.boolean(),
  /** Sub-accounts whose leads couldn't be read just now. */
  unreachableConnections: z.array(z.object({ connectionId: z.string().uuid(), locationName: z.string() })),
})
export type GhlCoupleListDto = z.infer<typeof ghlCoupleListDtoSchema>

/** What a picked lead fills in on the new-event form. Every part may be missing. */
export const ghlPrefillDtoSchema = z.object({
  first: z.string().nullable(),
  last: z.string().nullable(),
  partnerFirst: z.string().nullable(),
  partnerLast: z.string().nullable(),
  clientName: z.string().nullable(),
  clientEmail: z.string().nullable(),
  clientPhone: z.string().nullable(),
  guestCount: z.number().int().nonnegative().nullable(),
  /** The lead's details as staff notes; null when there are none. */
  notes: z.string().nullable(),
  /** The track the opportunity's connection sends its leads to. */
  trackId: z.string().uuid().nullable(),
})
export type GhlPrefillDto = z.infer<typeof ghlPrefillDtoSchema>

/** One couple's presence in one sub-account, as a review item keeps it: only GoHighLevel's ids. */
export const ghlCandidateRefSchema = z.object({
  connectionId: z.string().uuid(),
  locationId: z.string(),
  contactId: z.string(),
  opportunityIds: z.array(z.string()).min(1),
})
export type GhlCandidateRef = z.infer<typeof ghlCandidateRefSchema>

/** A review item's candidates: each is one couple, possibly in several sub-accounts. */
export const ghlCandidateRefListSchema = z.array(z.array(ghlCandidateRefSchema).min(1)).max(5)
export type GhlCandidateRefList = z.infer<typeof ghlCandidateRefListSchema>

/** What a backfill run did, event by event. */
export const ghlBackfillCountsSchema = z.object({
  considered: z.number().int().default(0),
  linkedByEmail: z.number().int().default(0),
  linkedByClef: z.number().int().default(0),
  sentToReview: z.number().int().default(0),
  unmatched: z.number().int().default(0),
  /** Clef wasn't set up or couldn't be reached; the next run tries these again. */
  notYetMatched: z.number().int().default(0),
})
export type GhlBackfillCounts = z.infer<typeof ghlBackfillCountsSchema>

export const ghlBackfillRunDtoSchema = z.object({
  id: z.string().uuid(),
  venueId: z.string().uuid(),
  status: ghlBackfillStatusSchema,
  counts: ghlBackfillCountsSchema,
  /** Event names only — never emails, phones or keys. */
  log: z.string(),
  error: z.string().nullable(),
  startedByName: z.string().nullable(),
  startedAt: instantSchema.nullable(),
  finishedAt: instantSchema.nullable(),
  createdAt: instantSchema,
})
export type GhlBackfillRunDto = z.infer<typeof ghlBackfillRunDtoSchema>

export const ghlBackfillRunListDtoSchema = z.object({ runs: z.array(ghlBackfillRunDtoSchema) })
export type GhlBackfillRunListDto = z.infer<typeof ghlBackfillRunListDtoSchema>

/** An event the backfill couldn't link on its own, with its candidates read live from GoHighLevel. */
export const ghlReviewItemDtoSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  eventName: z.string(),
  eventDate: calendarDaySchema.nullable(),
  reason: ghlReviewReasonSchema,
  /** Read from GoHighLevel now; empty when it can't be reached. */
  candidates: z.array(ghlCoupleDtoSchema),
  /** Index into `candidates` of Clef's pick; null when there is none or it can't be shown. */
  suggestedIndex: z.number().int().nullable(),
  suggestedProbability: z.number().nullable(),
  /** For `email_taken`: the event that couple is linked to now; null otherwise, or once it no longer is. */
  takenBy: z
    .object({ eventId: z.string().uuid(), eventName: z.string(), eventDate: calendarDaySchema.nullable() })
    .nullable(),
  createdAt: instantSchema,
})
export type GhlReviewItemDto = z.infer<typeof ghlReviewItemDtoSchema>

export const ghlReviewListDtoSchema = z.object({
  /** The oldest waiting, a page at a time: each is read from GoHighLevel as the list opens. */
  items: z.array(ghlReviewItemDtoSchema),
  /** How many are waiting in all. */
  total: z.number().int().nonnegative(),
})
export type GhlReviewListDto = z.infer<typeof ghlReviewListDtoSchema>
